import React, { Component } from 'react';
import ReactModal from 'react-modal';
import Loader from 'react-loader-spinner';
import { fetchInstallationCsvTemplate, importInstallationCsv } from '../../actions/installationAction';
import { downloadCsv } from './homeAudioUtils';

const MODAL_CLASS = { base: 'ha-csv-modal', afterOpen: 'ha-csv-modal--open', beforeClose: 'ha-csv-modal--closing' };
const OVERLAY_CLASS = { base: 'ha-drawer-ov', afterOpen: 'ha-drawer-ov--open', beforeClose: 'ha-drawer-ov--closing' };

// "+ Create Manual Request" (PRD-CONNECT §8): pick the channel, drop the CSV, read the
// summary, confirm. The file is kept in memory and posted again on confirm, so what gets
// imported is exactly what was previewed.
class HomeAudioCsvImportModal extends Component {
    state = {
        channels: [],
        channelId: '',
        file: null,
        template: null,
        summary: null,
        result: null,
        busy: false,
        error: null,
    };

    componentDidMount() {
        this.loadMasters();
    }

    // The template call also returns the ONLY channels a manual file may come from (Amazon,
    // Flipkart, Offline); the full channel master is never offered here.
    loadMasters = async () => {
        const template = await fetchInstallationCsvTemplate();
        this.setState({
            channels: template.success ? template.data.channels || [] : [],
            template: template.success ? template.data : null,
            error: template.success ? null : template.error,
        });
    };

    handleFile = (fileList) => {
        const file = fileList && fileList[0];
        if (!file) return;
        if (!/\.csv$/i.test(file.name)) {
            this.setState({ file: null, summary: null, error: 'Unsupported file type — please upload a .csv file.' });
            return;
        }
        this.setState({ file, summary: null, result: null, error: null }, this.preview);
    };

    buildForm = (confirm) => {
        const form = new FormData();
        form.append('csv', this.state.file);
        form.append('channel_id', this.state.channelId);
        form.append('confirm', confirm ? 'true' : 'false');
        return form;
    };

    preview = async () => {
        if (!this.state.file || !this.state.channelId) return;
        this.setState({ busy: true, error: null });
        const result = await importInstallationCsv(this.buildForm(false));
        this.setState({ busy: false, summary: result.success ? result.data : null, error: result.success ? null : result.error });
    };

    confirm = async () => {
        this.setState({ busy: true, error: null });
        const result = await importInstallationCsv(this.buildForm(true));
        if (!result.success) {
            this.setState({ busy: false, error: result.error });
            return;
        }
        this.setState({ busy: false, result: result.data, summary: null });
        if (this.props.onImported) this.props.onImported(result.data);
    };

    downloadTemplate = () => {
        const { template } = this.state;
        if (template) downloadCsv(template.sample_csv, template.file_name);
    };

    renderSummary = () => {
        const { summary, busy } = this.state;
        return (
            <div className="ha-csv-summary">
                <div className="ha-csv-stat-row">
                    <span className="ha-csv-stat ok">{summary.to_ticket_received} to Ticket Received</span>
                    <span className="ha-csv-stat warn">{summary.to_duplicate_bucket} to Duplicate Bucket</span>
                    <span className="ha-csv-stat bad">{summary.errors.length} with errors</span>
                </div>

                {summary.errors.length > 0 && (
                    <div className="ha-csv-list">
                        <div className="ha-csv-list-title">Rows with errors — skipped</div>
                        {summary.errors.map((e) => (
                            <div className="ha-csv-row bad" key={e.row}>
                                Row {e.row} ({e.name}): {e.issues.join(', ')}
                            </div>
                        ))}
                    </div>
                )}

                {summary.warnings && summary.warnings.length > 0 && (
                    <div className="ha-csv-list">
                        <div className="ha-csv-list-title">Product Model differs from SKU — the SKU was used</div>
                        {summary.warnings.map((w) => (
                            <div className="ha-csv-row warn" key={w.row}>
                                Row {w.row} ({w.name}): {w.message}
                            </div>
                        ))}
                    </div>
                )}

                {summary.duplicates.length > 0 && (
                    <div className="ha-csv-list">
                        <div className="ha-csv-list-title">
                            Possible duplicates — imported into Duplicate Bucket, review after import
                        </div>
                        {summary.duplicates.map((d) => (
                            <div className="ha-csv-row warn" key={d.row}>
                                Row {d.row} ({d.name}, {d.mobile}) matches{' '}
                                {d.existing_request_no ? <b>{d.existing_request_no}</b> : <b>row {d.duplicate_of_row} of this file</b>}
                            </div>
                        ))}
                    </div>
                )}

                <div className="ha-modal-actions">
                    <button className="ha-btn ha-btn-ghost" disabled={busy} onClick={() => this.setState({ file: null, summary: null })}>
                        Choose another file
                    </button>
                    <button className="ha-btn ha-btn-primary" disabled={busy || summary.ready === 0} onClick={this.confirm}>
                        {busy ? 'Importing…' : `Import ${summary.ready} request${summary.ready === 1 ? '' : 's'}`}
                    </button>
                </div>
            </div>
        );
    };

    renderResult = () => {
        const { result } = this.state;
        return (
            <div className="ha-csv-summary">
                <div className="ha-notice">
                    {result.created} request{result.created === 1 ? '' : 's'} imported: {result.to_ticket_received} to Ticket Received,{' '}
                    {result.to_duplicate_bucket} to Duplicate Bucket.
                </div>
                {result.failed && result.failed.length > 0 && (
                    <div className="ha-csv-list">
                        <div className="ha-csv-list-title">Could not be created</div>
                        {result.failed.map((f) => (
                            <div className="ha-csv-row bad" key={f.row}>
                                Row {f.row} ({f.name}): {f.reason}
                            </div>
                        ))}
                    </div>
                )}
                <div className="ha-modal-actions">
                    <button className="ha-btn ha-btn-primary" onClick={this.props.onClose}>
                        Done
                    </button>
                </div>
            </div>
        );
    };

    render() {
        const { onClose } = this.props;
        const { channels, channelId, file, template, summary, result, busy, error } = this.state;
        return (
            <ReactModal isOpen onRequestClose={onClose} className={MODAL_CLASS} overlayClassName={OVERLAY_CLASS} ariaHideApp={false}>
                <div className="ha-dr-head">
                    <div className="ha-dr-title">Create Manual Requests</div>
                    <button className="ha-dr-close" onClick={onClose} title="Close">
                        <i className="fas fa-times" />
                    </button>
                </div>

                <div className="ha-csv-body">
                    {result ? (
                        this.renderResult()
                    ) : (
                        <>
                            <label className="ha-dr-field">
                                <span>Step 1 — Source channel (applies to the whole file)</span>
                                <select
                                    className="ha-select"
                                    value={channelId}
                                    disabled={busy}
                                    onChange={(e) => this.setState({ channelId: e.target.value, summary: null }, this.preview)}
                                >
                                    <option value="">
                                        {channels.length ? 'Select the channel this CSV was collected from…' : 'No eligible channel (Amazon / Flipkart / Offline) found in the channel master'}
                                    </option>
                                    {channels.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            {channelId && (
                                <div className="ha-dr-field">
                                    <span>Step 2 — Upload CSV</span>
                                    <label className={`ha-csv-drop ${file ? 'has' : ''}`}>
                                        <input type="file" accept=".csv" hidden disabled={busy} onChange={(e) => this.handleFile(e.target.files)} />
                                        {file ? (
                                            <>
                                                <i className="fas fa-file-csv" /> <b>{file.name}</b>
                                            </>
                                        ) : (
                                            <>
                                                <i className="fas fa-upload" /> Click to choose a .csv file
                                            </>
                                        )}
                                    </label>
                                    <div className="ha-csv-help">
                                        {template && (
                                            <>
                                                Required headers: <code>{template.columns.join(', ')}</code>
                                                {template.optional_columns.length > 0 && <> · optional: <code>{template.optional_columns.join(', ')}</code></>}
                                                {' · '}max {template.max_rows} rows.{' '}
                                                <button className="ha-link" onClick={this.downloadTemplate}>
                                                    Download sample
                                                </button>
                                                {template.eligible_skus && template.eligible_skus.length > 0 ? (
                                                    <details className="ha-csv-skus">
                                                        <summary>
                                                            {template.eligible_skus.length} installable SKU{template.eligible_skus.length === 1 ? '' : 's'} accepted right now
                                                        </summary>
                                                        <ul>
                                                            {template.eligible_skus.map((entry) => (
                                                                <li key={entry.sku}>
                                                                    <code>{entry.sku}</code> {entry.product_name}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </details>
                                                ) : (
                                                    <div className="ha-error ha-error-inline">
                                                        No installable SKU is configured yet, so every row would be rejected. Check the Home Audio product group.
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {error && <div className="ha-error ha-error-inline">{error}</div>}
                            {busy && !summary && (
                                <div className="ha-loader">
                                    <Loader type="Oval" color="Grey" height={40} width={40} />
                                </div>
                            )}
                            {summary && this.renderSummary()}
                        </>
                    )}
                </div>
            </ReactModal>
        );
    }
}

export default HomeAudioCsvImportModal;
